
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { Loader2 } from "lucide-react";

interface PricingTierProps {
  title: string;
  price: number;
  features: string[];
  buttonText: string;
  buttonVariant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  isPopular?: boolean;
  isLoading?: boolean;
  buttonDisabled?: boolean;
  onButtonClick?: () => void;
  isCurrentPlan?: boolean;
  className?: string;
}

export function PricingTier({
  title,
  price,
  features,
  buttonText,
  buttonVariant = "default",
  isPopular = false,
  isLoading = false,
  buttonDisabled = false,
  onButtonClick,
  isCurrentPlan = false,
  className = ""
}: PricingTierProps) {
  const handleButtonClick = () => {
    if (onButtonClick && !buttonDisabled && !isLoading) {
      onButtonClick();
    }
  };

  const getCardClasses = () => {
    const baseClasses = "relative p-6 h-full flex flex-col transition-all duration-300";
    
    if (isPopular) {
      return `${baseClasses} border-2 border-app-accent bg-app-surface shadow-lg transform hover:scale-105`;
    }
    
    return `${baseClasses} border border-app-border bg-app-surface hover:border-app-accent hover:shadow-md ${className}`;
  };

  const getButtonClasses = () => {
    if (isCurrentPlan || buttonDisabled) {
      return "w-full bg-app-surface-elevated text-app-text-secondary border border-app-border cursor-not-allowed";
    }
    
    if (buttonVariant === "outline") {
      return "w-full border-app-border text-app-text-primary hover:bg-app-accent hover:text-app-accent-foreground hover:border-app-accent";
    }
    
    return "w-full bg-app-accent text-app-accent-foreground hover:bg-app-accent/90";
  };

  return (
    <Card className={getCardClasses()}>
      {isPopular && (
        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-app-accent text-app-accent-foreground px-3 py-1">
          Popular
        </Badge>
      )}
      
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold text-app-text-primary mb-2">{title}</h3>
        <div className="flex items-baseline justify-center">
          <span className="text-3xl font-bold text-app-text-primary">${price}</span>
          <span className="text-app-text-secondary ml-1">/month</span>
        </div>
      </div>
      
      <ul className="space-y-3 mb-6 flex-grow">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2">
            <Check className="w-5 h-5 text-app-accent mt-0.5 flex-shrink-0" />
            <span className="text-sm text-app-text-secondary">{feature}</span>
          </li>
        ))}
      </ul>
      
      <Button
        className={getButtonClasses()}
        onClick={handleButtonClick}
        disabled={buttonDisabled || isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading...
          </>
        ) : (
          buttonText
        )}
      </Button>
    </Card>
  );
}
